import React, { createContext, useEffect, useState } from "react";
import questionnaireReducer, { initialState } from "./QuestionnaireReducer";
import actions from "./questionnaireActions";
import useReducerWithMiddleware from "./useReducerWithMiddleware"; // Adjust the import path as needed
import useAuth from "../hooks/useAuth";
import { afterLoggingMiddleware, loggingMiddleware } from "./middleware";
import { db } from "../firebaseConfig";
import { doc, getDoc, setDoc } from "@firebase/firestore";

export const AppContext = createContext(initialState);

const saveDataContext = async (action, state, additionalData) => {
  const { type } = action;
  if (type === actions.RESET || type === actions.SET_QUESTIONNAIRE) {
    return;
  }

  const { user } = additionalData;

  if (user === undefined) {
    throw new Error("User is undefined");
  }

  const docRef = doc(db, "users", user.uid);
  let data = { ...state };

  delete data.isLoading;
  
  await setDoc(docRef, data, {
    merge: true,
  })
    .then(() => {
      //console.log("Document successfully written!");
    })
    .catch((error) => {
      console.error("Error writing document: ", error);
    });
};

export function AppProvider({ children }) {
  const [isLoaded, setIsLoaded] = useState(true);

  const { user } = useAuth();

  const [state, dispatch] = useReducerWithMiddleware(
    questionnaireReducer,
    initialState,
    [loggingMiddleware],
    [afterLoggingMiddleware, saveDataContext],
    { user: user }
  );

  useEffect(() => {
    const loadData = async () => {
      if (user) {
        try {
          const docRef = doc(db, "users", user.uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = { ...docSnap.data() };
            dispatch({ type: actions.LOAD_DATA, payload: data });
          }
          setIsLoaded(false);
        } catch (error) {
          setIsLoaded(true);
          console.error("Error getting document:", error);
        }
      }
    };

    loadData();

    return () => {
    };
  }, []);

  const setQuestionnaire = (questionnaire) => {
    //console.log("setQuestionnaire called with:", questionnaire);

    let existingQuestionnaires = [...state.questionnaires];
    const findQuestionnaireIndex = existingQuestionnaires.findIndex((q) => q.questionnaireId === questionnaire.questionnaireId);

    if (findQuestionnaireIndex !== -1) {
      const existingQuestionnaire = existingQuestionnaires[findQuestionnaireIndex];

      const updatedQuestionnaire = {
        ...existingQuestionnaire,
        ...questionnaire,
        answers: questionnaire.answers.length > 0 ? questionnaire.answers : existingQuestionnaire.answers || [],
      };

      existingQuestionnaires[findQuestionnaireIndex] = updatedQuestionnaire;
    } else {
      //console.log("Adding new questionnaire");
      questionnaire.answers = questionnaire.answers || [];
      existingQuestionnaires = [...existingQuestionnaires, questionnaire];
    }

    dispatch({
      type: actions.SET_QUESTIONNAIRE,
      payload: {
        questionnaires: existingQuestionnaires,
        currentQuestionnaire: questionnaire,
      },
    });

    //console.log("Questionnaires after setting:", existingQuestionnaires);
  };

  const saveChoice = async (choice, questionId, questionnaire) => {
    const existingQuestionnaires = state.questionnaires;
    const findQuestionnaire = existingQuestionnaires.find((q) => q.questionnaireId === questionnaire.questionnaireId);

    if (findQuestionnaire) {
      //console.log("findQuestionnaire", findQuestionnaire.answers);
      const existingAnswers = findQuestionnaire.answers;
      let findAnswer = existingAnswers.find((a) => a.id === questionId);
      if (findAnswer) {
        const newAnswer = {
          ...findAnswer,
          text: choice,
        };
        existingAnswers.splice(existingAnswers.indexOf(findAnswer), 1, newAnswer);
      } else {
        findQuestionnaire.answers.push({
          id: questionId,
          text: choice,
          questionText: questionnaire.questionnaireQuestions.find((q) => q.questionId === questionId).questionText,
          type: questionnaire.questionnaireQuestions.find((q) => q.questionId === questionId).type,
        });
      }

      existingQuestionnaires.splice(existingQuestionnaires.indexOf(findQuestionnaire), 1, findQuestionnaire);

      dispatch({
        type: actions.SAVE_CHOICE,
        payload: existingQuestionnaires,
      });
    }
  };

  const resetChoices = () => {
    dispatch({
      type: actions.RESET,
    });
  };

  const getAnswers = (questionnaireId) => {
    const questionnaire = state.questionnaires.find((q) => q.questionnaireId === questionnaireId);
    return questionnaire ? questionnaire.answers : [];
  };

  const getRankings = () => {
    if (!state.prioritizedYesIndexes || state.prioritizedYesIndexes.length === 0) {
      console.error("prioritizedYesIndexes is undefined or empty");
      return [];
    }

    const questionnaire = state.questionnaires[0];
    if (!questionnaire) return [];

    const identifiedBarriers = questionnaire.answers
      .filter((answer) => answer.text === "0")
      .map((answer, index) => ({
        text: answer.text,
        index,
        questionText: answer.questionText,
        type: answer.type,
      }));

    let rankings = state.prioritizedYesIndexes.map((index, rank) => {
      return {
        rank: rank + 1,
        barrier: identifiedBarriers.find((barrier) => barrier.index === index),
        type: identifiedBarriers.find((barrier) => barrier.index === index)?.type,
      };
    });

    // Filter out any rankings with undefined barriers or types
    rankings = rankings.filter((ranking) => ranking.barrier !== undefined && ranking.type !== undefined);

    dispatch({
      type: actions.ADD_RANKING,
      payload: rankings,
    });
    return rankings;
  };

  const setPrioritizedYesIndexes = (indexes) => {
    //console.log("yesIndexes", indexes);
    dispatch({
      type: actions.SET_PRIORITIZED_YES_INDEXES,
      payload: indexes,
    });
  };

  const allAnswered = (questionnaireId) => {
    const questionnaire = state.questionnaires.find((q) => q.questionnaireId === questionnaireId);
  
    // If questionnaire is not found, return false
    if (!questionnaire) {
      return false;
    }
  
    const questionnaireQuestions = questionnaire.questionnaireQuestions;
  
    // If questionnaire.questions is undefined or answers is undefined, return false
    if (!Array.isArray(questionnaireQuestions) || !Array.isArray(questionnaire.answers)) {
      return false;
    }
  
    // Check that all questions have an answer and that none of the answers are empty or null
    return questionnaireQuestions.every((question) => {
      const answer = questionnaire.answers.find((a) => a.id === question.questionId);
      return answer && answer.text !== null && answer.text !== "";
    });
  };
  

  const changeCurrentStep = (step) => {
    dispatch({
      type: actions.CHANGE_STEP,
      payload: step,
    });

    //console.log("Current Step:", state.currentStep);

    // Ensure this doesn't trigger any unintended side effects
    if (step === 1) {
      const currentQuestionnaire = state.currentQuestionnaire;
      if (currentQuestionnaire && currentQuestionnaire.answers) {
        setQuestionnaire(currentQuestionnaire); // Ensure answers are preserved
      }
    }
  };

  const removeRankingAndIndexes = async () => {
    if (!user) {
      throw new Error("User is undefined");
    }
  
    const docRef = doc(db, "users", user.uid);
    
    // Remove ranking and prioritizedYesIndexes from Firestore
    await setDoc(docRef, {
      ranking: [],
      prioritizedYesIndexes: []
    }, { merge: true })
      .then(() => {
        dispatch({ type: actions.REMOVE_RANKING_AND_INDEXES });
      })
      .catch((error) => {
        console.error("Error removing ranking and indexes from Firestore: ", error);
      });
  };

  return (
    <AppContext.Provider
      value={{
        questionnaires: state.questionnaires,
        currentQuestionnaire: state.currentQuestionnaire,
        currentStep: state.currentStep,
        ranking: state.ranking,
        isLoading: state.isLoading,
        prioritizedYesIndexes: state.prioritizedYesIndexes,
        setQuestionnaire,
        saveChoice,
        resetChoices,
        allAnswered,
        getAnswers,
        getRankings,
        setPrioritizedYesIndexes,
        changeCurrentStep,
        removeRankingAndIndexes,
        isLoaded,
        dispatch,
      }}
    >
      {children}
    </AppContext.Provider>
  );
}
